// Registry of admin-toggleable page sections — lets an admin hide/show
// individual content blocks per guest page (Admin > Website > Page
// Sections) without touching code. Reuses the generic site_settings
// key-value store (api/site_settings.php) rather than a dedicated table —
// each section gets a boolean-ish '0'/'1' setting, defaulting to visible
// when unset so this is purely additive and safe for existing content.
//
// Deliberately excludes structural elements every page needs regardless
// (the <Seo> tag, the page's own <PageHero>/hero) — hiding those would
// leave a page headerless or break its meta tags, not just remove a
// content block.
import { usePageSeoContext } from './PageSeoContext';

export interface PageSectionDef {
  key: string;
  page: string;
  label: string;
}

export const pageSectionRegistry: PageSectionDef[] = [
  // Home
  { key: 'home.trust_strip', page: 'Home', label: 'Trust Strip (stat cards)' },
  { key: 'home.why_choose_us', page: 'Home', label: 'Why Guests Choose Us' },
  { key: 'home.story', page: 'Home', label: 'Our Story' },
  { key: 'home.spaces_preview', page: 'Home', label: 'Spaces Preview' },
  { key: 'home.video', page: 'Home', label: 'Guest Interviews Video' },
  { key: 'home.map', page: 'Home', label: 'Map / Location Section' },
  { key: 'home.cta', page: 'Home', label: 'Bottom CTA' },

  // Rooms & Suites (the bedroom listing itself is generated per-room from
  // live room data, not a fixed section — not included here)
  { key: 'rooms.feature_chips', page: 'Rooms & Suites', label: 'Feature Chips' },
  { key: 'rooms.video', page: 'Rooms & Suites', label: 'Rooms Walkthrough Video' },
  { key: 'rooms.cta', page: 'Rooms & Suites', label: 'Bottom CTA' },

  // Spaces
  { key: 'spaces.tiles', page: 'Spaces', label: 'Explore the Space (admin-managed tiles carousel)' },
  { key: 'spaces.essentials', page: 'Spaces', label: 'Everyday Essentials Nearby' },
  { key: 'spaces.video', page: 'Spaces', label: 'Property Walkthrough Video' },
  { key: 'spaces.cta', page: 'Spaces', label: 'Bottom CTA' },

  // Reviews
  { key: 'reviews.ratings_strip', page: 'Reviews', label: 'Ratings Strip' },
  { key: 'reviews.video_slider', page: 'Reviews', label: 'Review Video Slider' },
  { key: 'reviews.photo_carousel', page: 'Reviews', label: 'Review Photo Carousel' },
  { key: 'reviews.quotes', page: 'Reviews', label: "In Guests' Own Words" },
  { key: 'reviews.cta', page: 'Reviews', label: 'Bottom CTA' },

  // Location
  { key: 'location.map', page: 'Location', label: 'Map' },
  { key: 'location.distances', page: 'Location', label: 'Distances at a Glance' },
  { key: 'location.nearby', page: 'Location', label: 'Nearby Shops & Essentials' },
  { key: 'location.video', page: 'Location', label: 'Neighborhood Walkthrough Video' },

  // Tourist Attractions
  { key: 'tourist_attractions.instructions', page: 'Tourist Attractions', label: 'How-to Instructions & Travel Tip' },
  { key: 'tourist_attractions.list', page: 'Tourist Attractions', label: 'Attractions List & Itinerary Planner' },
  { key: 'tourist_attractions.heritage', page: 'Tourist Attractions', label: 'Heritage Walk CTA' },

  // Things to Do in Gujarat
  { key: 'gujarat_places.instructions', page: 'Things to Do in Gujarat', label: 'How-to Instructions & Travel Note' },
  { key: 'gujarat_places.list', page: 'Things to Do in Gujarat', label: 'Destinations List & Itinerary Planner' },

  // About
  { key: 'about.story', page: 'About', label: 'Our Story' },
  { key: 'about.host', page: 'About', label: 'Meet Your Host' },
  { key: 'about.certificate', page: 'About', label: 'Gold Category Recognition' },
  { key: 'about.values', page: 'About', label: 'Our Core Values' },
  { key: 'about.video', page: 'About', label: 'Host Introduction Video' },
  { key: 'about.cta', page: 'About', label: 'Bottom CTA' },

  // Sustainability
  { key: 'sustainability.water', page: 'Sustainability', label: 'Responsible Resource Management' },
  { key: 'sustainability.energy', page: 'Sustainability', label: 'Smart Energy Use & Green Power' },
  { key: 'sustainability.safety', page: 'Sustainability', label: 'Safety & Security First' },
  { key: 'sustainability.video', page: 'Sustainability', label: 'Sustainability Walkthrough Video' },
  { key: 'sustainability.cta', page: 'Sustainability', label: 'Bottom CTA' },

  // FAQ
  { key: 'faq.accordion', page: 'FAQ', label: 'FAQ Accordion' },
  { key: 'faq.video', page: 'FAQ', label: 'Guest Guide Video' },

  // Contact
  { key: 'contact.info_form', page: 'Contact', label: 'Contact Info & Form' },
  { key: 'contact.video', page: 'Contact', label: 'Arrival Walkthrough Video' },
];

export function sectionSettingKey(key: string): string {
  return `section_visible__${key}`;
}

/** Reads whether a section should render — defaults to visible (true) when
 * the admin has never touched its toggle, so this is safe to wire into
 * existing pages without a migration or backfill. */
export function useSectionVisible(key: string): boolean {
  const { siteSettings } = usePageSeoContext();
  return siteSettings[sectionSettingKey(key)] !== '0';
}

export function getSectionPages(): string[] {
  return [...new Set(pageSectionRegistry.map((s) => s.page))];
}
